import { API_URL } from "./config";

/**
 * Map a workspace's `region` field to its regional API URL.
 *
 * Why this exists:
 * The default API URL = `https://api.ano.dev` resolves via CF DNS to
 * `api-us.ano.dev`. Every regional-PG mutation (CLI-key mint via
 * `/api/cli-keys`, `/mcp/list_workspaces`, /context, etc.) MUST go to
 * the workspace's actual home region — `api_keys` is foreign-keyed to
 * the local `workspaces(id)` and `workspace_members` is regional-only,
 * so a request to api-us with an EU workspace_id either FK-fails on
 * INSERT or 403s on the membership check.
 *
 * Two surfaces in the desktop call this:
 *   1. `use-cli-credentials-install.ts` — writes the external-terminal
 *      `~/.config/ano/credentials.json`. Fixed in v0.1.40.
 *   2. `provision-cli-key.ts` — sets `ANO_API_KEY` + `ANO_ENDPOINT` for
 *      the in-app embedded shell. Fixed in v0.1.41.
 *
 * Both consume the same helper to keep the routing rule in one place.
 *
 * Single-region beta (2026-05-18 cutover): in PRODUCTION the EU
 * authority is stopped (api-eu.ano.dev returns 502). If a legacy 'eu'
 * workspace row surfaces before the post-merge D1 coercion runs, we'd
 * mint CLI credentials pointing at a dead host. Hard-coerce to 'us'
 * in production as belt-and-suspenders. Staging and local dev keep
 * the legacy mapping so the EU staging stack (`api-staging.ano.dev`
 * → not eu, but staging is single-stack so this doesn't matter)
 * continues to work normally.
 * See .claude/plans/look-at-this-issue-imperative-nygaard.md Phase 1a.
 */
const IS_PRODUCTION_API =
  API_URL === "https://api.ano.dev" || API_URL === "https://api-us.ano.dev";

export function regionalApiUrl(region: string | null | undefined): string {
  if (IS_PRODUCTION_API) {
    return "https://api-us.ano.dev";
  }
  const r = region === "eu" ? "eu" : "us";
  return `https://api-${r}.ano.dev`;
}
